import React, { useCallback, useEffect, useState } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { getUsersApi, deleteUserApi, blockUserApi, approveUserKycApi } from "helpers/auth_api"
import UserDetailsModal, { UserDetailsModalUser } from "components/UserDetailsModal"

interface BreadcrumbItem {
  title: string
  link: string
}

interface UsersKycProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const breadcrumbItems: BreadcrumbItem[] = [
  { title: "Users", link: "#" },
  { title: "User List & KYC", link: "#" },
]

interface UserRow extends UserDetailsModalUser {
  createdAt?: string
  updatedAt?: string
}

const UsersKyc = (props: UsersKycProps) => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewUser, setViewUser] = useState<UserRow | null>(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserRow | null>(null)
  const [blockConfirmUser, setBlockConfirmUser] = useState<UserRow | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [kycActionLoading, setKycActionLoading] = useState(false)

  useEffect(() => {
    props.setBreadcrumbItems("User List & KYC", breadcrumbItems)
  }, [props])

  const fetchUsers = useCallback(() => {
    setLoading(true)
    setError(null)
    getUsersApi()
      .then((res) => {
        const list = res?.data ?? []
        setUsers((Array.isArray(list) ? list : []) as UserRow[])
      })
      .catch((err) => setError(err?.response?.data?.message || err?.message || "Failed to load users"))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const openDetailsModal = (user: UserRow) => {
    setViewUser(user)
    setDetailsModalOpen(true)
  }

  const closeDetailsModal = () => {
    setDetailsModalOpen(false)
    setViewUser(null)
  }

  const handleDeleteUser = () => {
    if (!deleteConfirmUser) return
    setActionLoading(true)
    deleteUserApi(deleteConfirmUser._id)
      .then((res) => {
        if (res.success) {
          toast.success("User deleted successfully")
          setDeleteConfirmUser(null)
          fetchUsers()
        } else {
          toast.error(res.message || "Failed to delete user")
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message || "Failed to delete user"))
      .finally(() => setActionLoading(false))
  }

  const runKycApproval = (userId: string, approved: boolean) => {
    setKycActionLoading(true)
    approveUserKycApi(userId, approved)
      .then((res) => {
        if (res.success) {
          toast.success(res.message || (approved ? "KYC approved" : "Approval removed"))
          fetchUsers()
          if (viewUser?._id === userId && res.data) {
            setViewUser({ ...viewUser, ...(res.data as UserRow) })
          }
        } else {
          toast.error(res.message || "Action failed")
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message || "Action failed"))
      .finally(() => setKycActionLoading(false))
  }

  const handleBlockUser = (blocked: boolean) => {
    if (!blockConfirmUser) return
    setActionLoading(true)
    blockUserApi(blockConfirmUser._id, blocked)
      .then((res) => {
        if (res.success) {
          toast.success(blocked ? "User blocked" : "User unblocked")
          setBlockConfirmUser(null)
          setDetailsModalOpen(false)
          setViewUser(null)
          fetchUsers()
        } else {
          toast.error(res.message || "Action failed")
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message || "Action failed"))
      .finally(() => setActionLoading(false))
  }

  return (
    <React.Fragment>
      <Row>
        <Col xs="12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <CardTitle tag="h5" className="mb-0">
                User List & KYC Status
              </CardTitle>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{users.length} user(s)</span>
                <Button color="primary" size="sm" onClick={() => navigate("/users/add")}>
                  <i className="mdi mdi-account-plus me-1" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {loading && <div className="alert alert-info">Loading users...</div>}
              {error && <div className="alert alert-danger">{error}</div>}
              {!loading && !error && (
                <div className="table-responsive">
                  <Table className="table table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 50 }}>S.No.</th>
                        <th style={{ width: 260 }}>Action</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Mobile</th>
                        <th style={{ width: 130 }}>KYC Status</th>
                        <th style={{ width: 100 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            No users found
                          </td>
                        </tr>
                      ) : (
                        users.map((user, idx) => (
                          <tr key={user._id}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => openDetailsModal(user)}
                                >
                                  <i className="mdi mdi-account-eye me-1" />
                                  View
                                </Button>
                                {(user.kycSubmitted === true ||
                                  !!(user.kycSelfieDone && user.kycSignatureDone)) &&
                                  !user.kycComplete && (
                                  <Button
                                    color="success"
                                    size="sm"
                                    outline
                                    disabled={kycActionLoading}
                                    onClick={() => runKycApproval(user._id, true)}
                                    title="Approve KYC"
                                  >
                                    <i className="mdi mdi-shield-check me-1" />
                                    Approve KYC
                                  </Button>
                                )}
                                <Button
                                  color="secondary"
                                  size="sm"
                                  outline
                                  onClick={() => navigate(`/users/edit/${user._id}`)}
                                >
                                  <i className="mdi mdi-pencil me-1" />
                                  Edit
                                </Button>
                                <Button
                                  color={user.isBlocked ? "success" : "warning"}
                                  size="sm"
                                  outline
                                  onClick={() => setBlockConfirmUser(user)}
                                  title={user.isBlocked ? "Unblock user" : "Block user"}
                                >
                                  <i className={`mdi mdi-${user.isBlocked ? "account-check" : "account-off"}`} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  onClick={() => setDeleteConfirmUser(user)}
                                  title="Delete user"
                                >
                                  <i className="mdi mdi-delete" />
                                </Button>
                              </div>
                            </td>
                            <td>
                              <strong>{user.fullName || "—"}</strong>
                            </td>
                            <td>{user.email || "—"}</td>
                            <td>{user.role || "—"}</td>
                            <td>{user.mobileNumber || "—"}</td>
                            <td>
                              {user.kycComplete ? (
                                <Badge color="success">Approved</Badge>
                              ) : user.kycSubmitted === true ||
                                !!(user.kycSelfieDone && user.kycSignatureDone) ? (
                                <Badge color="warning">Pending approval</Badge>
                              ) : (
                                <Badge color="secondary">Not submitted</Badge>
                              )}
                            </td>
                            <td>
                              {user.isBlocked ? (
                                <Badge color="danger">Blocked</Badge>
                              ) : (
                                <Badge color="success">Active</Badge>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <UserDetailsModal
        isOpen={detailsModalOpen}
        toggle={closeDetailsModal}
        user={viewUser}
        showKycAdminActions
        kycActionLoading={kycActionLoading}
        onApproveKyc={() => viewUser && runKycApproval(viewUser._id, true)}
        onRevokeKyc={() => viewUser && runKycApproval(viewUser._id, false)}
      />

      {/* Delete user confirmation */}
      <Modal isOpen={!!deleteConfirmUser} toggle={() => !actionLoading && setDeleteConfirmUser(null)} centered>
        <ModalHeader toggle={() => !actionLoading && setDeleteConfirmUser(null)}>Delete User</ModalHeader>
        <ModalBody>
          {deleteConfirmUser && (
            <>
              <p className="mb-0">
                Are you sure you want to delete <strong>{deleteConfirmUser.fullName || deleteConfirmUser.email}</strong>? This action cannot be undone.
              </p>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <Button color="secondary" onClick={() => setDeleteConfirmUser(null)} disabled={actionLoading}>
                  Cancel
                </Button>
                <Button color="danger" onClick={handleDeleteUser} disabled={actionLoading}>
                  {actionLoading ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      {/* Block / Unblock user confirmation */}
      <Modal isOpen={!!blockConfirmUser} toggle={() => !actionLoading && setBlockConfirmUser(null)} centered>
        <ModalHeader toggle={() => !actionLoading && setBlockConfirmUser(null)}>
          {blockConfirmUser?.isBlocked ? "Unblock User" : "Block User"}
        </ModalHeader>
        <ModalBody>
          {blockConfirmUser && (
            <>
              <p className="mb-0">
                {blockConfirmUser.isBlocked
                  ? `Unblock ${blockConfirmUser.fullName || blockConfirmUser.email}? They will be able to login again.`
                  : `Block ${blockConfirmUser.fullName || blockConfirmUser.email}? They will not be able to login until unblocked.`}
              </p>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <Button color="secondary" onClick={() => setBlockConfirmUser(null)} disabled={actionLoading}>
                  Cancel
                </Button>
                <Button
                  color={blockConfirmUser.isBlocked ? "success" : "warning"}
                  onClick={() => handleBlockUser(!blockConfirmUser.isBlocked)}
                  disabled={actionLoading}
                >
                  {actionLoading ? "..." : blockConfirmUser.isBlocked ? "Unblock" : "Block"}
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

const mapStateToProps = () => ({})
const mapDispatchToProps = (dispatch: any) => ({
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) =>
    dispatch(setBreadcrumbItems(title, items)),
})

export default connect(mapStateToProps, mapDispatchToProps)(UsersKyc)
